import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Brightness4 as Brightness4Icon } from '@mui/icons-material';
import { Menu as MenuIcon, X as CloseIcon } from 'lucide-react';
import logo from '../images/rodeoLogo.png';

const GuestLayout = ({ children, darkMode, setDarkMode }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const navItems = [
    { label: 'Features', path: '/features' },
    { label: 'Pricing', path: '/pricing' },
  ];

  // Mobile Drawer
  const MobileDrawer = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={handleDrawerToggle}
      PaperProps={{
        sx: {
          width: 240,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgb(13, 34, 55)' : '#fff',
          pt: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 1 }}>
        <IconButton onClick={handleDrawerToggle} color="inherit">
          <CloseIcon size={20} />
        </IconButton>
      </Box>

      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton onClick={() => handleNavigation(item.path)}>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 1 }} />

      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation('/login')}>
            <ListItemText
              primary="Log In"
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { setDarkMode(!darkMode); setDrawerOpen(false); }}>
            <Brightness4Icon fontSize="small" sx={{ opacity: 0.7 }} />
          </ListItemButton>
        </ListItem>
        <ListItem sx={{ px: 2, pt: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleNavigation('/signup')}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
            }}
          >
            Sign Up For Free
          </Button>
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <div>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ px: { xs: 2, sm: 4 }, gap: 2 }}>

          {/* Logo + Brand */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src={logo}
              alt="MarketRodeo"
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #00d4ff, #0099ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              MarketRodeo
            </Typography>
          </Box>

          {/* Middle nav — Features, Pricing */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 4, mr: 'auto' }}>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    opacity: 0.85,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right side — auth + dark mode */}
          {!isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                color="inherit"
                component={Link}
                to="/login"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  opacity: 0.85,
                  '&:hover': { opacity: 1 },
                }}
              >
                Log In
              </Button>

              <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />

              <Button
                variant="contained"
                component={Link}
                to="/signup"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  borderRadius: 2,
                  px: 2.5,
                  py: 0.75,
                  background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
                  boxShadow: '0 2px 10px rgba(25,118,210,0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 15px rgba(25,118,210,0.4)',
                  },
                }}
              >
                Sign Up For Free
              </Button>

              <IconButton
                onClick={() => setDarkMode(!darkMode)}
                color="inherit"
                size="small"
                sx={{ opacity: 0.85, '&:hover': { opacity: 1 } }}
              >
                <Brightness4Icon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ ml: 'auto' }}>
              <IconButton
                color="inherit"
                onClick={handleDrawerToggle}
                edge="end"
              >
                <MenuIcon size={22} />
              </IconButton>
            </Box>
          )}

        </Toolbar>
      </AppBar>

      <MobileDrawer />

      <main>{children}</main>
    </div>
  );
};

export default GuestLayout;